import { useQueryClient } from "@tanstack/react-query";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { GrayTableButton, PrimaryTableButton } from "components/Button/TableButton";
import { ButtonProps } from "@chakra-ui/react";
import React from "react";
import TooltipWrapper from "components/TooltipWrapper";
import { issueKeys } from "features/Issues/hooks";
import { ActionSummaryData, ActionV2 } from "features/Resources/Shared/models";
import { getKeySetByPrefixId, useForceSendByPrefixId } from "features/Resources/Shared/hooks";
import { fromActionPrefixId } from "features/Resources/Shared/helpers";
import { confirmSendNowArgs } from "features/Resources/Shared/helpers/confirmationArgs";

interface SendNowButtonProps extends ButtonProps {
  action: ActionV2 | ActionSummaryData;
  onSuccess?: (resp: any) => void;
}

export function SendNowButtonWrapper({ action, onSuccess, children }: SendNowButtonProps) {
  const queryClient = useQueryClient();
  const { resourceType } = fromActionPrefixId(action?.prefixId);

  const { mutate: performRequest } = useForceSendByPrefixId(
    action?.prefixId,
    (resp: any) => {
      queryClient.invalidateQueries({ queryKey: issueKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.details() });
      // TODO: set this in the cache not invalidate
      queryClient.invalidateQueries({ queryKey: getKeySetByPrefixId(action?.prefixId)?.detail(action.id) });
      onSuccess?.(resp);
    },
  );

  const isDisabled = !action;
  const { isDisabled: childIsDisabled } = (children as JSX.Element).props;

  return (
    <TooltipWrapper
      condition={childIsDisabled || isDisabled}
      tooltipText="This action cannot be sent until you've resolved all other issues."
    >
      <ButtonWithConfirm
        handleConfirm={() => performRequest()}
        intent="confirmation"
        {...confirmSendNowArgs(resourceType)}
      >
        {React.cloneElement(
          children as JSX.Element,
          { isDisabled: childIsDisabled || isDisabled },
        )}
      </ButtonWithConfirm>
    </TooltipWrapper>
  );
}

export function GraySendNowButton({ action, onSuccess, ...rest }: SendNowButtonProps) {
  const { resourceType } = fromActionPrefixId(action?.prefixId);
  return (
    <SendNowButtonWrapper action={action} onSuccess={onSuccess}>
      <GrayTableButton {...rest}>{resourceType === "task" ? "Start Now" : "Send Now"}</GrayTableButton>
    </SendNowButtonWrapper>
  );
}

export function PrimarySendNowButton({ action, onSuccess, ...rest }: SendNowButtonProps) {
  const { resourceType } = fromActionPrefixId(action?.prefixId);
  return (
    <SendNowButtonWrapper action={action} onSuccess={onSuccess}>
      <PrimaryTableButton {...rest}>{resourceType === "task" ? "Start Now" : "Send Now"}</PrimaryTableButton>
    </SendNowButtonWrapper>
  );
}
